import React, { useState, useEffect } from 'react';
import Modal from '../../components/Modal';
import { supabase } from '../../utils/supabaseClient';
import { v4 as uuidv4 } from 'uuid';

function ConnectWebsite({ isOpen, onClose }) {
  const [form, setForm] = useState({
    cms: '',
    domain: '',
    username: '',
    password: '',
    shop_name: '',
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [shopifyConnected, setShopifyConnected] = useState(false);

  const testWordPressConnection = async () => {
    setConnectionStatus(null);

    try {
      let domainUrl = form.domain.trim();
      if (!domainUrl.startsWith('http')) {
        domainUrl = 'https://' + domainUrl;
      }

      const url = `${domainUrl.replace(/\/$/, '')}/wp-json/wp/v2/users/me`;
      const auth = btoa(`${form.username}:${form.password}`);

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Basic ${auth}`,
        },
      });

      if (res.status === 401) {
        setConnectionStatus('failed');
        throw new Error('Invalid credentials');
      }

      if (!res.ok) {
        throw new Error('Connection failed');
      }

      setConnectionStatus('success');
    } catch (err) {
      console.error(err.message);
      setConnectionStatus('failed');
    }
  };

  useEffect(() => {
    if (isOpen) {
      setForm({
        cms: '',
        domain: '',
        username: '',
        password: '',
        shop_name: '',
      });
      setErrors({});
      setSubmitError('');
      setConnectionStatus(null);
      setShopifyConnected(false);
    } else {
      const timer = setTimeout(() => {
        setConnectionStatus(null);
        setShopifyConnected(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'cms') {
      // Reset form when CMS type changes
      setForm({
        cms: value,
        domain: '',
        username: '',
        password: '',
        shop_name: '',
      });
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    setErrors((prev) => ({ ...prev, [name]: false }));
    setSubmitError('');
  };

  const validate = () => {
    const newErrors = {};
    if (!form.cms) newErrors.cms = true;
    if (!form.domain.trim()) newErrors.domain = true;

    if (form.cms === 'wordpress') {
      if (!form.username.trim()) newErrors.username = true;
      if (!form.password.trim()) newErrors.password = true;
    }

    if (form.cms === 'shopify') {
      if (!form.shop_name.trim()) newErrors.shop_name = true;
      if (!form.password.trim()) newErrors.password = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      let domainUrl = form.domain.trim();
      if (!domainUrl.startsWith('http')) {
        domainUrl = 'https://' + domainUrl;
      }

      const payload = {
        id: uuidv4(),
        user_id: user.id,
        cms_type: form.cms,
        domain: domainUrl,
        username: form.cms === 'wordpress' ? form.username : null,
        app_password: form.password,
        shop_name: form.cms === 'shopify' ? form.shop_name : null,
        created_at: new Date().toISOString(),
      };

      const { error } = await supabase.from('websites').insert(payload);

      if (error) {
        console.error('Error saving website:', error.message);
        setSubmitError(error.message);
        return;
      }

      if (form.cms === 'shopify') {
        setShopifyConnected(true);
        setTimeout(() => {
          onClose();
          setShopifyConnected(false);
        }, 2000);
      } else {
        onClose();
      }
    } catch (err) {
      console.error(err.message);
      setSubmitError(err.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Connect a Website"
      actions={[
        ...(form.cms === 'wordpress'
          ? [{
              label: 'Test Connection',
              className: 'btn-outlined',
              onClick: testWordPressConnection,
              disabled: !form.domain || !form.username || !form.password,
            }]
          : []),
        ...((form.cms === 'shopify' || connectionStatus === 'success')
          ? [{
              label:
                submitting
                  ? 'Connecting...'
                  : shopifyConnected
                    ? 'Connected!'
                    : 'Connect',
              className: 'btn-outlined',
              onClick: handleCreate,
              disabled: submitting || shopifyConnected,
            }]
          : []),
        {
          label: 'Cancel',
          className: 'btn-outlined',
          onClick: onClose,
        },
      ]}
    >
      <form className="create-item" onSubmit={(e) => e.preventDefault()}>
        <label>
          CMS Type:
          {errors.cms && <span className="req-message">Required</span>}
        </label>
        <select name="cms" value={form.cms} onChange={handleChange}>
          <option value="">Select CMS</option>
          <option value="wordpress">WordPress</option>
          <option value="shopify">Shopify</option>
        </select>

        {form.cms === 'wordpress' && (
          <>
            <label>
              Domain:
              {errors.domain && <span className="req-message">Required</span>}
            </label>
            <input
              type="text"
              name="domain"
              value={form.domain}
              onChange={handleChange}
              placeholder="https://example.com"
            />
            <label>
              Username:
              {errors.username && <span className="req-message">Required</span>}
            </label>
            <input
              type="text"
              name="username"
              value={form.username}
              onChange={handleChange}
              placeholder="admin"
            />
            <label className="mb-0">
              Application Password:
              {errors.password && <span className="req-message">Required</span>}
            </label>
            <p className="label-note">Generated in WordPress: Users <i className="bi bi bi-chevron-right"></i> Profile</p>
            <input
              type="text"
              name="password"
              value={form.password}
              onChange={handleChange}
              placeholder="application-specific password"
            />
          </>
        )}

        {form.cms === 'shopify' && (
          <>
            <label>
              Domain:
              {errors.domain && <span className="req-message">Required</span>}
            </label>
            <input
              type="text"
              name="domain"
              value={form.domain}
              onChange={handleChange}
              placeholder="https://example.com"
            />
            <label>
              Shopify Store Name:
              {errors.shop_name && <span className="req-message">Required</span>}
            </label>
            <input
              type="text"
              name="shop_name"
              value={form.shop_name}
              onChange={handleChange}
              placeholder="your-store-name (no .myshopify.com)"
            />
            <label className="mb-0">
              Private Admin API Token:
              {errors.password && <span className="req-message">Required</span>}
            </label>
            <p className="label-note">Log into your Shopify Admin to generate a Private Admin API token.</p>
            <input
              type="text"
              name="password"
              value={form.password}
              onChange={handleChange}
              placeholder="shpat_..."
            />
          </>
        )}

        {submitError && (
          <p className="error-message" style={{ marginTop: '1rem' }}>
            {submitError}
          </p>
        )}

        {form.cms === 'wordpress' && connectionStatus === 'success' && (
          <p>✅ Connection successful!</p>
        )}
        {form.cms === 'wordpress' && connectionStatus === 'failed' && (
          <p>❌ Authentication failed. Please check your credentials.</p>
        )}
      </form>
    </Modal>
  );
}

export default ConnectWebsite;
